import React, { useLayoutEffect } from "react";
import { Option, Select } from "@natera/form";
import classNames from "classnames";
import { HeapPHIDecorator } from "@app/components/analytics";
import {
  SetValidationError,
  ClearValidationError,
} from "@natera/platform/lib/hooks/useError";
import { NoScrollFormField } from "@app/components/ui/inputs/noScrollFormField";

import { type MonthsMapOption } from "./months";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  dateOfBirthMonthLabel: {
    id: "dateOfBirthVerificationMonthLabel",
    defaultMessage: "Month",
  },
});

const LABEL_ID = "dob-month-select-label";
const OPTION_ID_PREFIX = "dob-month-option-";
const ERROR_ID = "dob-month-error";

interface DobMonthFieldProps {
  monthsMap: MonthsMapOption[];
  selectedMonth: MonthsMapOption | null;
  selectedDay?: number;
  selectedYear?: string;
  showLabel?: boolean;
  disabled?: boolean;

  getValidationError: (field: string) => string | undefined;
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;

  setSelectedMonth: (month: MonthsMapOption | null) => void;
}

// Switched from TypeAhead to Select — the typeahead race between blur, focus,
// and the dropdown menu made it very hard to reliably keep valid selections
// AND clear invalid ones (see UPP-5711). Select is click-only, so the "typed
// invalid text lingers" bug isn't possible: there's no way to enter an
// invalid value in the first place. Validation of "did the user pick a
// month?" is handled at the DobInput level, unchanged.
const DobMonthField: React.FC<DobMonthFieldProps> = ({
  monthsMap,
  selectedMonth,
  showLabel = true,
  disabled,
  getValidationError,
  clearValidationError,
  setSelectedMonth,
}) => {
  const intl = useIntl();
  const monthError = getValidationError("dateOfBirthMonth");

  // Patch ARIA on the Material-emitted DOM to match what stateField.tsx is doing
  useLayoutEffect(() => {
    const inputEl = document.getElementById("dateOfBirthMonth");
    const anchor =
      inputEl?.closest<HTMLElement>(".mdc-select__anchor") ??
      inputEl
        ?.closest<HTMLElement>(".mdc-select")
        ?.querySelector<HTMLElement>(".mdc-select__anchor");
    if (!anchor) return;

    anchor.setAttribute("aria-haspopup", "listbox");

    const updateAriaActivedescendant = () => {
      if (selectedMonth) {
        const optionId = `${OPTION_ID_PREFIX}${selectedMonth.value}`;
        const optionEl = document.getElementById(optionId);
        if (optionEl) {
          anchor.setAttribute("aria-activedescendant", optionId);
          return;
        }
      }
      anchor.removeAttribute("aria-activedescendant");
    };

    const observer = new MutationObserver(() => {
      // The Material menu portals into document.body when it opens, so we
      // find and label its listbox from there.
      const listboxes = document.querySelectorAll<HTMLElement>(
        ".mdc-select__menu .mdc-deprecated-list"
      );
      listboxes.forEach((listbox) => {
        if (!listbox.hasAttribute("role")) {
          listbox.setAttribute("role", "listbox");
          listbox.setAttribute("aria-labelledby", LABEL_ID);
        }
      });
      updateAriaActivedescendant();
    });

    updateAriaActivedescendant();
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [selectedMonth]);

  return (
    <NoScrollFormField
      label={
        showLabel
          ? intl.formatMessage(messages.dateOfBirthMonthLabel)
          : undefined
      }
      error={monthError}
      htmlFor="dateOfBirthMonth"
      labelId={LABEL_ID}
      className={classNames("dob-input__month", {
        "dob-verification-form__invalid":
          getValidationError("dateOfBirthIncorrect") ||
          getValidationError("dateOfBirthRequired"),
      })}
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap={true}>
        <Select
          id="dateOfBirthMonth"
          name="dateOfBirthMonth"
          data-testid="dateOfBirthMonth"
          className="dob-input__input"
          labelId={LABEL_ID}
          role="combobox"
          outlined
          required
          floating
          placeholder={intl.formatMessage(messages.dateOfBirthMonthLabel)}
          value={selectedMonth ? String(selectedMonth.value) : undefined}
          onValueChange={(value: string | string[] | number) => {
            const strValue = Array.isArray(value) ? value[0] : String(value);
            const match =
              monthsMap.find((m) => String(m.value) === strValue) ?? null;
            setSelectedMonth(match);
            if (match) {
              clearValidationError("dateOfBirthMonth");
              clearValidationError("dateOfBirthIncorrect");
            }
          }}
          disabled={disabled}
          aria-describedby={monthError ? ERROR_ID : undefined}
        >
          {monthsMap.map((option) => (
            <Option
              key={option.value}
              id={`${OPTION_ID_PREFIX}${option.value}`}
              value={String(option.value)}
              aria-selected={selectedMonth?.value === option.value}
            >
              {option.label}
            </Option>
          ))}
        </Select>
      </HeapPHIDecorator>
    </NoScrollFormField>
  );
};

export default DobMonthField;
